import { defineComponent as _defineComponent } from 'vue'
import { ref, computed } from 'vue'
import { useMutation } from '@vue/apollo-composable'
import { useNuxtApp, useRoute } from '#app'
import SetAccountUserRoleDocument from '~/graphql/mutations/account/SetAccountUserRole.gql'
import {
  modules,
  sortModulesAlphabetically,
  usageTypes,
  formatPercentage,
} from '~/assets/helper/functions'
import {
  AccountCategory,
  AccountMembershipRole,
  Module,
  useGetAccountQuery,
  useGetUserQuery,
  useListAccountsQuery,
  useUpdateAccountMutation,
  type ListAccountFilter,
} from '~/types/types'
import { useReviewRouteGuards } from '~/functions/review'


export default /*#__PURE__*/_defineComponent({
  __name: 'accounts',
  setup(__props) {

const { $i18n } = useNuxtApp()

useReviewRouteGuards({ guardAdminAccounts: true })

const headers = computed(() =>
  [
    {
      text: '',
      value: 'logo',
      width: '48px',
      sortable: false,
    },
    {
      value: 'id',
      cellClass: 'font-weight-bold',
      sortable: false,
    },
    {
      value: 'name',
      cellClass: 'font-weight-bold',
      sortable: true,
    },
    {
      value: 'type',
    },
    {
      value: 'usageType',
    },
    {
      value: 'modules',
      cellClass: 'overflow-auto',
    },
    {
      value: 'users',
      cellClass: 'overflow-auto',
    },
    {
      text: 'Expiry date',
      value: 'expiryDate',
      sortable: false,
    },
    {
      value: 'createdAt',
      sortable: true,
    },
    { text: $i18n.t('actions'), value: 'actions' },
  ].map((col) => ({ ...col, sortable: col.sortable || false })),
)

const options = ref({
  page: 1,
  itemsPerPage: 10,
  sortBy: ['createdAt'],
  sortDesc: [true],
})

const moduleByKey = {
  [Module.Esg]: 'ESG',
  [Module.Metrics]: 'KPI',
  [Module.Sfdr]: 'SFDR(FUND)',
  [Module.Csrd]: 'CSRD',
  [Module.ImpactGoal]: 'Impact',
  [Module.Annotation]: 'Annotation',
  [Module.Impact]: 'Deprecated Impact',
  [Module.ImpactStrategy]: 'Impact Strategy',
  [Module.Review]: 'Review',
}

const sortedModules = (mods: Module[] | undefined | null) =>
  sortModulesAlphabetically(mods, moduleByKey)

const membershipRolesForUser = (
  account: { accountMemberships?: { role: AccountMembershipRole; user: { id: string } }[] },
  userId: string,
) => {
  const roles = account.accountMemberships
    ?.filter((entry) => entry.user.id === userId)
    .map((entry) => entry.role)
  if (roles && roles.length > 0) {
    return roles
  }
  return [AccountMembershipRole.Editor]
}

const hasAccountAdminRole = computed(() => {
  const roles = ((account.value as any)?.membershipRoles || []) as string[]
  return roles.includes('ADMIN')
})

const currentUserCanAssignAdmin = computed(
  () => currentUserIsIxAdmin.value || hasAccountAdminRole.value,
)

const normalizeRoles = (
  roles: AccountMembershipRole | AccountMembershipRole[] | null | undefined,
) => {
  if (Array.isArray(roles)) {
    return roles
  }
  if (roles) {
    return [roles]
  }
  return [AccountMembershipRole.Editor]
}

const onRolesChange = (
  item: { id: string },
  userId: string,
  roles: AccountMembershipRole | AccountMembershipRole[] | null | undefined,
) =>
  setAccountUserRoles({
    accountId: item.id,
    userId,
    roles: normalizeRoles(roles),
  }).then(() => refetch())

const filters = ref({
  type: [AccountCategory.Portfolio, AccountCategory.Venture],
  usageType: usageTypes.map((e) => e.value),
  period: '2024',
  expiryPresence: 'ALL',
  expiryStatusFilter: 'ALL',
})

const onExpiryFilterChange = () => {
  options.value.page = 1
  if (filters.value.expiryPresence !== 'WITH_EXPIRY') {
    filters.value.expiryStatusFilter = 'ALL'
  }
}

const route = useRoute()

const variables = computed(() => {
  const listFilters = { ...(filters.value as ListAccountFilter) }
  if ((listFilters as any).expiryPresence !== 'WITH_EXPIRY') {
    ;(listFilters as any).expiryStatusFilter = 'ALL'
  }
  return {
    filters: {
      ...listFilters,
      keyword: route.query.search,
    },
    offset: options.value.itemsPerPage * (options.value.page - 1),
    limit: options.value.itemsPerPage,
    sort: options.value.sortBy.map((item, index) => ({
      field: item,
      sortDesc: options.value.sortDesc[index],
    })),
  }
})

const { result: accountResult } = useGetAccountQuery()
const account = computed(() => accountResult.value?.getAccount)
const portfolioId = computed(() =>
  account.value && account.value.id ? account.value.id : undefined,
)
const usageTypeFilterLength = computed(
  () => (filters.value && filters.value.usageType && filters.value.usageType.length) || 0,
)
const moduleFilterLength = computed(
  () => (filters.value && filters.value.module && filters.value.module.length) || 0,
)

const { result: userResult } = useGetUserQuery()
const user = computed(() => userResult.value?.getUser || {})
// IMPORTANT: `user.isAdmin` means IX admin (platform-level), not account membership ADMIN.
const isIxAdminSession = computed(
  () => Boolean(user.value?.isAdmin) && !user.value?.isImpersonated,
)
const showScopedAccountsTable = computed(
  () => (!user.value?.isAdmin || user.value?.isImpersonated) && Boolean(account.value),
)
const showGlobalAdminAccountsTable = computed(() => isIxAdminSession.value)
const currentUserIsIxAdmin = computed(() => isIxAdminSession.value)

const { result, refetch, loading } = useListAccountsQuery(
  variables,
  computed(() => ({
    enabled: showGlobalAdminAccountsTable.value,
  })),
)
const { mutate: updateAccount } = useUpdateAccountMutation({})
const { mutate: setAccountUserRoles } = useMutation(SetAccountUserRoleDocument)

const items = computed(() => result.value?.listAccounts ?? [])
const itemCount = computed(() => result.value?.countAccounts ?? 0)

const editedItem = ref<any>({})

const userDialog = ref(false)

const selectedUsers = ref<any[]>([])

const nonAdminColumns = computed(() => {
  const cols = [
    'logo',
    'name',
    'bookedModules',
    'portfolioLinks.groupNames',
    'usersCount',
    'provisioningStatus',
  ]

  const venturesWithMetrics = account.value?.ventureLinks?.filter((v: any) =>
    v?.modules?.includes(Module.Metrics),
  )

  if (venturesWithMetrics && venturesWithMetrics.length > 0) {
    return cols.concat([
      'metricAssignmentsCount',
      'completionRates.completionRate',
      'completionRates.skippedRate',
      'completionRates.responseRate',
      'createdAt',
      'actions',
    ])
  }

  return cols.concat(['createdAt', 'actions'])
})

const impersonate = (id: string) => {
  sessionStorage.setItem('impersonation_user_id', id)
  window.location.href = '/'
}

const formatExpiryDate = (value: string) => {
  // GraphQL Date is YYYY-MM-DD; avoid timezone shifting via Date parsing.
  if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
    return value.slice(0, 10)
  }
  return value
}

return { __sfc: true,$i18n, headers, options, moduleByKey, sortedModules, membershipRolesForUser, hasAccountAdminRole, currentUserCanAssignAdmin, normalizeRoles, onRolesChange, filters, onExpiryFilterChange, route, variables, accountResult, account, portfolioId, usageTypeFilterLength, moduleFilterLength, userResult, user, isIxAdminSession, showScopedAccountsTable, showGlobalAdminAccountsTable, currentUserIsIxAdmin, result, refetch, loading, updateAccount, setAccountUserRoles, items, itemCount, editedItem, userDialog, selectedUsers, nonAdminColumns, impersonate, formatExpiryDate, modules, usageTypes, AccountCategory, Module }
}

})